import { Radians } from "@/libs/units/angle";
import { clamp } from "@/utils/utils";

export function inclinationBetweenPointsDeg(
  prevMdFt: number,
  currentMdFt: number,
  prevTvdFt: number,
  currentTvdFt: number
): number {
  // Default to 0 if md diff is 0 (divide by 0)
  if (currentMdFt === 0) {
    return 0;
  }
  const angleRad = (currentTvdFt - prevTvdFt) / (currentMdFt - prevMdFt) || 0;
  return angleRad > 1 || angleRad < -1
    ? 0
    : Radians.toStandardUnit(Math.acos(angleRad));
}

export function getMeasuredDepthFor2DFt(
  currentTrueVerticalDepthFt: number,
  previousTrueVerticalDepthFt: number,
  currentInclinationDeg: number,
  previousMeasuredDepthFt: number
): number {
  if (currentInclinationDeg === 0) {
    return currentTrueVerticalDepthFt;
  }
  const tvd_change = currentTrueVerticalDepthFt - previousTrueVerticalDepthFt;
  const md_change =
    tvd_change / Math.cos(Radians.toThisUnit(currentInclinationDeg));
  return md_change + previousMeasuredDepthFt;
}

export function getTrueVerticalDepthFor2DFt(
  currentMeasuredDepthFt: number,
  previousMeasuredDepthFt: number,
  currentInclinationDeg: number,
  previousTrueVerticalDepthFt: number
): number {
  const md_change = currentMeasuredDepthFt - previousMeasuredDepthFt;
  const tvd_change =
    md_change * Math.cos(Radians.toThisUnit(currentInclinationDeg));
  return tvd_change + previousTrueVerticalDepthFt;
}

function calculateDogLegSeverityAndRatioFactor(
  currentInclinationDeg: number,
  previousInclinationDeg: number,
  currentAzimuthDeg: number,
  previousAzimuthDeg: number
): { dogLegSeverityRad: number; ratioFactor: number; cosineSum: number } {
  const currentInclinationRad = Radians.toThisUnit(currentInclinationDeg);
  const previousInclinationRad = Radians.toThisUnit(previousInclinationDeg);
  const currentAzimuthRad = Radians.toThisUnit(currentAzimuthDeg);
  const previousAzimuthRad = Radians.toThisUnit(previousAzimuthDeg);

  const dogLegSeverityRad = Math.acos(
    Math.cos(currentInclinationRad - previousInclinationRad) -
      Math.sin(previousInclinationRad) *
        Math.sin(currentInclinationRad) *
        (1 - Math.cos(currentAzimuthRad - previousAzimuthRad))
  );

  let ratioFactor = 1.0;
  if (dogLegSeverityRad !== 0) {
    ratioFactor = (2 / dogLegSeverityRad) * Math.tan(dogLegSeverityRad / 2);
  }

  const cosineSum =
    Math.cos(previousInclinationRad) + Math.cos(currentInclinationRad);

  return { dogLegSeverityRad, ratioFactor, cosineSum };
}

export function getMeasuredDepthFor3DFt(
  currentTrueVerticalDepthFt: number,
  previousTrueVerticalDepthFt: number,
  currentInclinationDeg: number,
  previousInclinationDeg: number,
  previousMeasuredDepthFt: number,
  currentAzimuthDeg: number,
  previousAzimuthDeg: number
): number {
  const tvdChange = currentTrueVerticalDepthFt - previousTrueVerticalDepthFt;

  const { ratioFactor } = calculateDogLegSeverityAndRatioFactor(
    currentInclinationDeg,
    previousInclinationDeg,
    currentAzimuthDeg,
    previousAzimuthDeg
  );

  const numerator = tvdChange / ratioFactor;
  const denominator =
    Math.cos(Radians.toThisUnit(previousInclinationDeg)) +
    Math.cos(Radians.toThisUnit(currentInclinationDeg));
  const measuredDepthFt =
    (numerator / denominator) * 2 + previousMeasuredDepthFt;

  return measuredDepthFt;
}

export function getTrueVerticalDepthFor3DFt(
  currentMeasuredDepthFt: number,
  previousMeasuredDepthFt: number,
  currentInclinationDeg: number,
  previousInclinationDeg: number,
  previousTrueVerticalDepthFt: number,
  currentAzimuthDeg: number,
  previousAzimuthDeg: number
): number {
  const mdChange = currentMeasuredDepthFt - previousMeasuredDepthFt;

  const { ratioFactor, cosineSum } = calculateDogLegSeverityAndRatioFactor(
    currentInclinationDeg,
    previousInclinationDeg,
    currentAzimuthDeg,
    previousAzimuthDeg
  );

  const TVDchange = (mdChange / 2) * cosineSum * ratioFactor;

  return TVDchange + previousTrueVerticalDepthFt;
}

export function getDogLegSeverityDeg100Ft(
  prevMdFt: number,
  curMdFt: number,
  prevInclDeg: number,
  curInclDeg: number,
  prevAzDeg: number,
  curAzDeg: number
): number {
  const mdDiffFt = curMdFt - prevMdFt;
  // Don't divide by 0 at the end, just return 0
  if (mdDiffFt === 0) return 0;
  // Equation written for degrees, trig funcs expect radians

  const prevInclRad = Radians.toThisUnit(prevInclDeg);
  const curInclRad = Radians.toThisUnit(curInclDeg);
  const prevAzRad = Radians.toThisUnit(prevAzDeg);
  const curAzRad = Radians.toThisUnit(curAzDeg);

  // Convert back to degrees
  const dlc = Radians.toStandardUnit(
    Math.acos(
      clamp(
        Math.cos(prevInclRad) * Math.cos(curInclRad) +
          Math.sin(prevInclRad) *
            Math.sin(curInclRad) *
            Math.cos(curAzRad - prevAzRad),
        -1,
        1
      )
    )
  );

  return dlc * (100 / mdDiffFt);
}

// These calcs are from /static/Friction Calcs foo.xlsx
export function getRowPhiRad(inclinationAngleDeg: number): number {
  return Radians.toThisUnit(90 - inclinationAngleDeg);
}
